export type RatingCorner = 'tech' | 'phys' | 'psych' | 'social';

export interface RatingInput {
  corner_id: string;
  score: number;
}

export type WeightMatrix = Record<RatingCorner, number>;

export interface CompositePlayerContext {
  maturity_offset?: number | null;
}

export interface CompositeOptions {
  ratings: RatingInput[];
  weights: WeightMatrix;
  player?: CompositePlayerContext;
}

const CORNERS: RatingCorner[] = ['tech', 'phys', 'psych', 'social'];
const DEFAULT_PSYCH_SOCIAL_FLOOR = 40;

export const DEFAULT_WEIGHT_MATRIX: WeightMatrix = {
  tech: 25,
  phys: 25,
  psych: 25,
  social: 25,
};

export function validateWeightMatrix(weights: WeightMatrix): string | null {
  for (const corner of CORNERS) {
    const value = weights[corner];
    if (!Number.isFinite(value)) return `${corner} weight must be a number.`;
    if (value < 0 || value > 100) return `${corner} weight must be between 0 and 100.`;
  }
  const total = CORNERS.reduce((sum, corner) => sum + weights[corner], 0);
  if (Math.abs(total - 100) > 0.01) return 'Corner weights must sum to 100.';
  return null;
}

export function normaliseCornerId(cornerId: string): RatingCorner | null {
  const clean = cornerId.trim().toLowerCase();
  if (/technical|tactical|\btech\b/.test(clean)) return 'tech';
  if (/physical|\bphys\b/.test(clean)) return 'phys';
  if (/psychological|\bpsych\b/.test(clean)) return 'psych';
  if (/social/.test(clean)) return 'social';
  return null;
}

function clampScore(score: number): number {
  if (!Number.isFinite(score)) return 0;
  return Math.max(0, Math.min(100, score));
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function adjustPhysicalForMaturity(score: number, maturityOffset?: number | null): number {
  if (maturityOffset == null || !Number.isFinite(maturityOffset)) return clampScore(score);
  // TODO(spec §07): Confirm the production maturity-offset curve once the
  // bio-banding model is final. This conservative curve trims early physical
  // advantage and protects late maturers without moving a score by >8 points.
  const adjustment = Math.max(-8, Math.min(8, maturityOffset * -4));
  return clampScore(score + adjustment);
}

export function applyPsychSocialFloor(
  rawComposite: number,
  psychScore: number,
  socialScore: number,
  floor = DEFAULT_PSYCH_SOCIAL_FLOOR,
): number {
  const lowest = Math.min(clampScore(psychScore), clampScore(socialScore));
  if (lowest >= floor) return clampScore(rawComposite);
  const penalty = (floor - lowest) * 0.5;
  return clampScore(rawComposite - penalty);
}

export function computeComposite(opts: CompositeOptions): number {
  const weightError = validateWeightMatrix(opts.weights);
  if (weightError) throw new Error(weightError);

  const buckets: Record<RatingCorner, number[]> = {
    tech: [],
    phys: [],
    psych: [],
    social: [],
  };

  for (const rating of opts.ratings) {
    const corner = normaliseCornerId(rating.corner_id);
    if (!corner) continue;
    buckets[corner].push(clampScore(rating.score));
  }

  const cornerScores: Record<RatingCorner, number> = {
    tech: mean(buckets.tech),
    phys: adjustPhysicalForMaturity(mean(buckets.phys), opts.player?.maturity_offset),
    psych: mean(buckets.psych),
    social: mean(buckets.social),
  };

  const raw = CORNERS.reduce(
    (sum, corner) => sum + cornerScores[corner] * (opts.weights[corner] / 100),
    0,
  );
  return Math.round(applyPsychSocialFloor(raw, cornerScores.psych, cornerScores.social));
}
